import { defineStore } from "pinia";
import { computed, ref, watch } from "vue";
import type { RouteLocationRaw } from "vue-router";
import { useRouter } from "vue-router";
import * as Sentry from "@sentry/vue";

import api from "@/utils/axios";
import createLogger from "@/utils/logger";
import type { UpcomingStop } from "@/stores/scheduled-stops";
import { dialog } from "@/components/Dialog";

const logger = createLogger("Account");

interface Account {
  id: number;
  first_name: string;
  last_name: string;
  email: string;
  phone_number: string;
  is_yepler: boolean;
  is_external_mechanic: boolean;
  current_scheduled_stop: UpcomingStop | null;
  default_van: {
    id: string;
    name: string;
    starting_location: {
      id: number;
      name: string;
      latitude: number;
      longitude: number;
    } | null;
  };
}

interface AccountUpdatePayload {
  first_name?: string;
  last_name?: string;
  email?: string;
  phone_number?: string;
}

interface CountryConfig {
  currency: {
    code: string;
    locale: string;
    symbol: string;
  };
  region: string;
}

export const useAccountStore = defineStore("account", () => {
  const router = useRouter();

  const account = ref<Account>();
  const countryConfig = ref<CountryConfig>();

  const selectedStop = ref(account.value?.current_scheduled_stop);
  const isViewingDifferentStop = computed(
    () =>
      selectedStop.value &&
      selectedStop.value?.id !== account.value?.current_scheduled_stop?.id,
  );

  watch(
    () => account.value?.current_scheduled_stop,
    (newVal) => {
      selectedStop.value = newVal;
    },
  );

  watch(
    selectedStop,
    (newValue, oldValue) => {
      if (!newValue) return;
      if (newValue.id === oldValue?.id) return;

      const currentSite = window.location.hostname;
      const stopSite = selectedStop.value?.site?.domain;
      if (stopSite && currentSite !== stopSite) {
        dialog({
          title: "Switch site?",
          message: `The stop you have selected is scheduled on <strong>${stopSite}</strong> but you are on <strong>${currentSite}</strong>. Please open Vantool on that site.`,
          html: true,
          ok: {
            label: "Switch site",
            noCaps: true,
            unelevated: true,
            rounded: true,
            color: "green",
            textColor: "black",
          },
          cancel: {
            label: "Cancel",
            noCaps: true,
            flat: true,
            color: "black",
          },
          class: "bg-primary text-black",
        }).onOk(() => {
          window.location.replace(`https://${stopSite}`);
        });
      }
    },
    { immediate: true },
  );

  async function getCsrfToken() {
    try {
      await api.get("/internal/auth/oidc/csrf");
    } catch (error: any) {
      error.message = `Failed getting CSRF token: ${error.message}`;
      logger.error(error.message);
      throw error;
    }
  }

  async function login(redirectToPath: string = "/") {
    logger.debug("Logging in with redirect to:", redirectToPath);
    window.location.href = `/api/internal/auth/oidc/login/vantool?next=${window.location.origin + redirectToPath}`;
  }

  const isLoggingOut = ref(false);

  async function logout(redirectPathAfterLogin = "/logged-out") {
    logger.debug("Logging out");
    if (isLoggingOut.value) {
      logger.debug(
        "Aborting logout because another logout is already in progress.",
      );
      return;
    }

    isLoggingOut.value = true;
    try {
      const next = window.location.origin + redirectPathAfterLogin;
      const response = await api.post(
        "/internal/auth/oidc/logout?next=" + next,
      );
      const { logout_url } = response.data;
      Sentry.setUser(null);
      window.location.href = logout_url;
    } catch (error: any) {
      router.push(redirectPathAfterLogin);
    } finally {
      isLoggingOut.value = false;
    }
  }

  async function requestPasswordReset(email: string, redirectPath: string) {
    try {
      await api.post("/auth/password/request-reset", {
        email,
        password_reset_url: window.location.origin + redirectPath,
      });
    } catch (error: any) {
      error.message = `Failed sending password reset link: ${error.message}`;
      throw error;
    }
  }

  async function verifyPasswordResetToken(uid: string, token: string) {
    try {
      await api.post("/auth/password/validate-reset-token", { uid, token });
      return true;
    } catch {
      return false;
    }
  }

  async function resetPassword(
    uid: string,
    token: string,
    newPassword: string,
  ) {
    try {
      await api.post("/auth/password/reset", {
        uid,
        token,
        new_password: newPassword,
      });
    } catch (error: any) {
      error.message = `Failed resetting password: ${error.message}`;
      throw error;
    }
  }

  async function getAccount() {
    try {
      const response = await api.get("/internal/vantool/account");
      account.value = response.data;
      Sentry.setUser({ id: account.value?.id, email: account.value?.email });
      applySentryRegionTag();
    } catch (error: any) {
      error.message = `Failed loading account: ${error.message}`;
      logger.error(error.message);
      throw error;
    }
  }

  function applySentryRegionTag() {
    const region = countryConfig.value?.region;
    if (region) {
      Sentry.setTag("region", region);
    }
    Sentry.setTag("site", window.location.hostname);
  }

  async function updateAccount(
    payload: AccountUpdatePayload,
    updateStore = true,
  ) {
    if (!account.value) throw Error("Account not loaded");

    try {
      const updatedAccount = await api.patch(
        `/internal/vantool/account`,
        payload,
      );
      logger.info("Account updated successfully", {
        payload,
        updatedAccount: account.value,
      });
      if (updateStore) {
        account.value = updatedAccount.data;
      }
    } catch (error: any) {
      error.message = `Failed updating account: ${error.message}`;
      logger.error(error.message);
      throw error;
    }
  }

  async function getCountryConfig() {
    let response;
    try {
      response = await api.get("/country-config/");
    } catch (error: any) {
      error.message = `Failed loading country config: ${error.message}`;
      logger.error(error.message);
      throw error;
    }

    countryConfig.value = response.data;
    applySentryRegionTag();
  }

  const isNlOrStaging = computed(
    () =>
      window.location.hostname.includes("yeply.nl") ||
      window.location.hostname === "stag.yeply.fi" ||
      window.location.hostname.includes("localhost"),
  );

  return {
    account,
    selectedStop,
    isViewingDifferentStop,
    getCsrfToken,
    login,
    logout,
    requestPasswordReset,
    verifyPasswordResetToken,
    resetPassword,
    getAccount,
    updateAccount,

    countryConfig,
    getCountryConfig,
    isNlOrStaging,
  };
});
